import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useContent } from "../lib/content";

const INITIAL = {
  promoter_name: "",
  email: "",
  phone: "",
  event_name: "",
  event_date: "",
  venue: "",
  city: "",
  country: "",
  capacity: "",
  message: "",
};

const CONTACT_EMAIL_DEFAULT = "soundationtechnoevents@gmail.com";

export default function Booking() {
  const kicker = useContent("page.booking.kicker", "/ 10 — BOOKING");
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const title = useContent("page.booking.title", "BOOKING INQUIRY");
  const intro = useContent(
    "page.booking.intro",
    "Inner Pulse is currently booking international clubs and festivals for 2026. Only name and email are required — the more details you share, the faster we can respond."
  );
  const successMsg = useContent(
    "page.booking.success_msg",
    "Your inquiry has been delivered. We'll be in contact shortly."
  );
  const privacyNote = useContent(
    "page.booking.privacy_note",
    "By submitting, your details will be shared only with Inner Pulse artist management."
  );
  const CONTACT_EMAIL = useContent("contact.email", CONTACT_EMAIL_DEFAULT);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.promoter_name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/submissions", { ...form, kind: "booking" });
      setSent(true);
      setForm(INITIAL);
      toast.success("Request sent. Artist management will respond soon.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="booking-page" className="px-6 md:px-12 py-20">
      <div className="max-w-[1200px] mx-auto">
        <div className="mb-16" data-testid="booking-header">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-silver-400" />
            <span className="font-title text-[0.65rem] tracking-[0.4em] text-silver-400">
              {kicker}
            </span>
          </div>
          <h1 className="font-title text-silver-glow text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-[0.06em] whitespace-pre-line">
            {title}
          </h1>
          <p className="font-body text-silver-400 text-sm md:text-base max-w-xl mt-8 leading-relaxed">
            {intro}
          </p>
          <p className="font-body text-silver-500 text-xs mt-4">
            Direct contact:{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-silver-200 hover:text-silver-100 underline underline-offset-4"
              data-testid="booking-direct-email"
            >
              {CONTACT_EMAIL}
            </a>
          </p>
        </div>

        {sent ? (
          <div
            className="hairline-t pt-10 flex flex-col gap-6 items-start"
            data-testid="booking-success"
          >
            <div className="font-title text-[0.65rem] tracking-[0.3em] text-silver-400">
              / REQUEST RECEIVED
            </div>
            <h2 className="font-title text-3xl text-silver-100 tracking-[0.1em]">
              Thank you.
            </h2>
            <p className="font-body text-silver-400 text-sm max-w-md">
              {successMsg}
            </p>
            <button
              className="btn-ghost"
              onClick={() => setSent(false)}
              data-testid="booking-send-another"
            >
              SEND ANOTHER
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="hairline-t pt-10 grid md:grid-cols-2 gap-x-10 gap-y-8"
            data-testid="booking-form"
          >
            <Field label="Promoter / Name *">
              <input
                className="ip-input"
                value={form.promoter_name}
                onChange={update("promoter_name")}
                required
                data-testid="booking-name"
              />
            </Field>
            <Field label="Email *">
              <input
                type="email"
                className="ip-input"
                value={form.email}
                onChange={update("email")}
                required
                data-testid="booking-email"
              />
            </Field>
            <Field label="Phone">
              <input
                className="ip-input"
                value={form.phone}
                onChange={update("phone")}
                data-testid="booking-phone"
              />
            </Field>
            <Field label="Event Name">
              <input
                className="ip-input"
                value={form.event_name}
                onChange={update("event_name")}
                data-testid="booking-event-name"
              />
            </Field>
            <Field label="Event Date">
              <input
                type="date"
                className="ip-input"
                value={form.event_date}
                onChange={update("event_date")}
                data-testid="booking-event-date"
              />
            </Field>
            <Field label="Venue">
              <input
                className="ip-input"
                value={form.venue}
                onChange={update("venue")}
                data-testid="booking-venue"
              />
            </Field>
            <Field label="City">
              <input
                className="ip-input"
                value={form.city}
                onChange={update("city")}
                data-testid="booking-city"
              />
            </Field>
            <Field label="Country">
              <input
                className="ip-input"
                value={form.country}
                onChange={update("country")}
                data-testid="booking-country"
              />
            </Field>
            <div className="md:col-span-2">
              <Field label="Expected Capacity">
                <input
                  className="ip-input"
                  value={form.capacity}
                  onChange={update("capacity")}
                  placeholder="e.g. 500"
                  data-testid="booking-capacity"
                />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="Message">
                <textarea
                  className="ip-input resize-none"
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about the event, vibe, lineup…"
                  data-testid="booking-message"
                />
              </Field>
            </div>
            <div className="md:col-span-2 flex items-center justify-between flex-wrap gap-4 pt-4">
              <p className="font-body text-silver-600 text-xs">
                {privacyNote}
              </p>
              <button
                type="submit"
                className="btn-sharp"
                disabled={loading}
                data-testid="booking-submit"
              >
                {loading ? "Sending…" : "Send Inquiry"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="ip-label">{label}</span>
      {children}
    </label>
  );
}
